"use client";

import { useEffect, useState } from "react";
import { AeraButton } from "@aera/ui";
import {
  MoneyUndoConfirmSheet,
  filterHistoryDetailsForDisplay,
} from "@aera/data-table";
import { showToast } from "@/app/components/ui/Toast";
import {
  fetchUndoImpact,
  formatUndoBlockMessage,
  postUndoCorrection,
  type UndoImpact,
} from "@/lib/moneyOperations";
import {
  clearLatestMoneyUndo,
  readLatestMoneyUndo,
  subscribeLatestMoneyUndo,
  type LatestMoneyUndo,
} from "@/lib/latestMoneyUndo";

function formatUndoOccurredAt(iso: string | undefined): string {
  if (!iso?.trim()) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function undoPreviewDetailLines(
  impact: UndoImpact | null,
  busy: boolean,
): string[] {
  if (!impact) {
    return busy ? ["กำลังโหลดรายละเอียด…"] : ["จะย้อนกลับการแก้ไขล่าสุด"];
  }
  if (impact.blocked || !impact.ok) {
    return impact.summary_lines?.length
      ? impact.summary_lines
      : [formatUndoBlockMessage(impact)];
  }
  const details = filterHistoryDetailsForDisplay(impact.details);
  if (details.length) return details;
  return (impact.summary_lines || []).filter(
    (line) => line !== impact.description,
  );
}

export function LatestMoneyUndoBar({
  workspaceId,
  onUndone,
}: {
  workspaceId: string;
  onUndone?: () => void | Promise<void>;
}) {
  const [entry, setEntry] = useState<LatestMoneyUndo | null>(null);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [impact, setImpact] = useState<UndoImpact | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    setEntry(readLatestMoneyUndo());
    return subscribeLatestMoneyUndo(setEntry);
  }, []);

  // If the remembered undo target was already undone, hide the bar without a click.
  useEffect(() => {
    if (!entry) return;
    if (entry.workspaceId !== workspaceId.trim()) return;
    let cancelled = false;
    void fetchUndoImpact({
      ownerUserId: entry.ownerUserId,
      targetOperationId: entry.operationId,
    })
      .then((next) => {
        if (cancelled) return;
        if (
          next.blocked ||
          !next.ok ||
          next.reason === "ALREADY_UNDONE" ||
          next.reason === "LATER_OPERATION_EXISTS" ||
          next.reason === "not_undoable"
        ) {
          clearLatestMoneyUndo();
        }
      })
      .catch(() => {
        /* keep bar; user can still try */
      });
    return () => {
      cancelled = true;
    };
  }, [entry?.operationId, entry?.ownerUserId, entry?.workspaceId, workspaceId]);

  if (!entry) return null;
  if (entry.workspaceId !== workspaceId.trim()) return null;

  const closePreview = () => {
    if (busy) return;
    setPreviewOpen(false);
    setImpact(null);
    setPreviewError(null);
  };

  const previewLines = undoPreviewDetailLines(impact, busy);
  const occurredLabel = formatUndoOccurredAt(impact?.occurred_at);

  const handleRequestUndo = async () => {
    setBusy(true);
    setPreviewError(null);
    setImpact(null);
    setPreviewOpen(true);
    try {
      const next = await fetchUndoImpact({
        ownerUserId: entry.ownerUserId,
        targetOperationId: entry.operationId,
      });
      setImpact(next);
      if (next.blocked || !next.ok) {
        setPreviewError(formatUndoBlockMessage(next));
        if (
          next.reason === "ALREADY_UNDONE" ||
          next.reason === "LATER_OPERATION_EXISTS" ||
          next.reason === "not_undoable"
        ) {
          clearLatestMoneyUndo();
        }
      }
    } catch (err) {
      setImpact(null);
      setPreviewError(
        err instanceof Error ? err.message : "โหลดผลกระทบการย้อนกลับไม่สำเร็จ",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmUndo = async () => {
    if (!impact || impact.blocked || !impact.ok) {
      setPreviewError(formatUndoBlockMessage(impact));
      return;
    }
    setBusy(true);
    try {
      await postUndoCorrection({
        ownerUserId: entry.ownerUserId,
        workspaceId: entry.workspaceId,
        targetOperationId: entry.operationId,
      });
      clearLatestMoneyUndo();
      setPreviewOpen(false);
      setImpact(null);
      showToast("ย้อนกลับสำเร็จ");
      await onUndone?.();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "ย้อนกลับไม่สำเร็จ";
      setPreviewError(message);
      showToast(message, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="shrink-0 py-2 px-4 min-[900px]:px-6">
        <div className="border border-aera-amber/30 bg-aera-amber/10 rounded-lg p-2 flex flex-wrap items-center justify-between gap-2">
          <p className="min-w-0 flex-1 text-xs font-medium text-aera-charcoal">
            ธุรกรรมล่าสุด: {entry.label}
          </p>
          <div className="flex items-center gap-2">
            <AeraButton
              type="button"
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() => void handleRequestUndo()}
            >
              {busy && !previewOpen ? "กำลังโหลด…" : "ย้อนกลับ"}
            </AeraButton>
            <AeraButton
              type="button"
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() => clearLatestMoneyUndo()}
            >
              ปิด
            </AeraButton>
          </div>
        </div>
      </div>

      <MoneyUndoConfirmSheet
        isOpen={previewOpen}
        onClose={closePreview}
        busy={busy}
        title={impact?.title || entry.label}
        detailLines={previewLines}
        metaLine={
          [impact?.channel_label, occurredLabel].filter(Boolean).join(" · ") ||
          null
        }
        error={previewError}
        blocked={Boolean(impact && (impact.blocked || !impact.ok))}
        blockedMessage="ไม่สามารถย้อนกลับได้ตอนนี้"
        onConfirm={() => void handleConfirmUndo()}
      />
    </>
  );
}
